import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { useState, type ReactNode } from 'react'

interface QueryProviderProps {
  children: ReactNode
}

/**
 * Query provider with sensible defaults for auth-related caching
 */
export function QueryProvider({ children }: QueryProviderProps) {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            // Stale time: 5 minutes for auth data
            staleTime: 5 * 60 * 1000,
            // Cache time: 30 minutes (keep in cache longer)
            gcTime: 30 * 60 * 1000,
            // Retry configuration
            retry: (failureCount, error) => {
              // Don't retry on 401/403 errors
              if (
                error instanceof Error &&
                'statusCode' in error &&
                [401, 403].includes((error as { statusCode: number }).statusCode)
              ) {
                return false
              }
              return failureCount < 3
            },
            // Refetch on window focus (good for auth state)
            refetchOnWindowFocus: true,
          },
          mutations: {
            // Never retry a write: a lost response after the api committed looks the same as a request that never arrived.
            retry: false,
          },
        },
      })
  )

  return (
    <QueryClientProvider client={queryClient}>
      {children}
      {/* DevTools only in development */}
      <ReactQueryDevtools initialIsOpen={false} position="bottom" />
    </QueryClientProvider>
  )
}
